import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DatePicker, Select, Spin, Switch, Modal, Button } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { GetMarketplaceList } from "../../../../config";
import { DEMO_DASHBOARD_DATE_MAX } from "../../../../utils/dashboardDemoDateRange";
import { DashFilterBarRoot, Wrapper, WrapperModal } from "./style";

const { Option } = Select;
const { RangePicker } = DatePicker;

const AMAZON_DATE_FORMAT = "YYYY-MM-DD";
const DISPLAY_DATE_FORMAT = "DD-MM-YYYY";

const DEFAULT_PRODUCT_IMAGE =
  "https://app.sellerview.ai/sellerview_api/images/no-product-image.png";

const PRIMARY_KEYS = ["today", "yesterday", "7d", "mtd", "30d", "custom"];
const COMPARE_KEYS = ["today", "yesterday", "7d", "mtd", "30d", "custom"];

const LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  mtd: "This Month",
  "7d": "7 Days",
  "30d": "30 Days",
  custom: "Custom",
};

const COMPARE_LABELS = {
  ...LABELS,
  mtd: "Last Month",
};

function anchorDay(isDemo) {
  return isDemo ? DEMO_DASHBOARD_DATE_MAX : dayjs();
}

function rangeForPreset(key, isDemo) {
  const a = anchorDay(isDemo).clone();
  switch (key) {
    case "today":
      return [a, a];
    case "yesterday":
      return [a.subtract(1, "day"), a.subtract(1, "day")];
    case "mtd":
      return [a.startOf("month"), a];
    case "7d":
      return [a.subtract(6, "day"), a];
    case "30d":
      return [a.subtract(29, "day"), a];
    default:
      return null;
  }
}

/** Same-length window immediately before the given range (inclusive). */
function previousPeriodRange(start, end) {
  const s = dayjs.isDayjs(start) ? start : dayjs(start);
  const e = dayjs.isDayjs(end) ? end : dayjs(end);
  if (!s.isValid() || !e.isValid()) return null;
  const days = e.diff(s, "day") + 1;
  const compareEnd = s.subtract(1, "day");
  const compareStart = compareEnd.subtract(days - 1, "day");
  return [compareStart, compareEnd];
}

function formatRangePair(range) {
  return {
    start_date: range[0].format(AMAZON_DATE_FORMAT),
    end_date: range[1].format(AMAZON_DATE_FORMAT),
  };
}

/** Default compare preset + dates from the active primary selection. */
function getDefaultCompareForPrimary(activePrimary, otherRange, isDemo) {
  switch (activePrimary) {
    case "today": {
      const r = rangeForPreset("yesterday", isDemo);
      return { key: "yesterday", ...formatRangePair(r) };
    }
    case "yesterday": {
      const r = rangeForPreset("today", isDemo);
      return { key: "today", ...formatRangePair(r) };
    }
    case "7d":
    case "mtd":
    case "30d": {
      const pr = rangeForPreset(activePrimary, isDemo);
      const prev = previousPeriodRange(pr[0], pr[1]);
      return { key: activePrimary, ...formatRangePair(prev) };
    }
    case "custom": {
      if (!otherRange?.start_date || !otherRange?.end_date) {
        const r = rangeForPreset("yesterday", isDemo);
        return { key: "yesterday", ...formatRangePair(r) };
      }
      const prev = previousPeriodRange(otherRange.start_date, otherRange.end_date);
      if (!prev) {
        const r = rangeForPreset("yesterday", isDemo);
        return { key: "yesterday", ...formatRangePair(r) };
      }
      return { key: "custom", ...formatRangePair(prev) };
    }
    default: {
      const r = rangeForPreset("yesterday", isDemo);
      return { key: "yesterday", ...formatRangePair(r) };
    }
  }
}

/** Resolve compare dates when user picks a compare pill (may differ from anchor presets). */
function resolveCompareRange(compareKey, activePrimary, otherRange, isDemo) {
  if (compareKey === "custom") {
    if (otherRange?.start_date && otherRange?.end_date) {
      return previousPeriodRange(otherRange.start_date, otherRange.end_date);
    }
    return rangeForPreset("yesterday", isDemo);
  }

  if (activePrimary === "today" && compareKey === "yesterday") {
    return rangeForPreset("yesterday", isDemo);
  }
  if (activePrimary === "yesterday" && compareKey === "today") {
    return rangeForPreset("today", isDemo);
  }
  if (
    (activePrimary === "7d" && compareKey === "7d") ||
    (activePrimary === "mtd" && compareKey === "mtd") ||
    (activePrimary === "30d" && compareKey === "30d")
  ) {
    const pr = rangeForPreset(activePrimary, isDemo);
    return previousPeriodRange(pr[0], pr[1]);
  }
  if (activePrimary === "custom" && compareKey === "custom" && otherRange?.start_date && otherRange?.end_date) {
    return previousPeriodRange(otherRange.start_date, otherRange.end_date);
  }

  return rangeForPreset(compareKey, isDemo);
}

function inferPresetKey(range, isDemo, allowedKeys) {
  if (!range?.start_date || !range?.end_date) return "custom";
  const a = anchorDay(isDemo);
  const s = dayjs(range.start_date);
  const e = dayjs(range.end_date);
  if (!s.isValid() || !e.isValid()) return "custom";

  const candidates = [];
  if (allowedKeys.includes("today")) {
    candidates.push(["today", s.isSame(a, "day") && e.isSame(a, "day")]);
  }
  if (allowedKeys.includes("yesterday")) {
    const y = a.subtract(1, "day");
    candidates.push(["yesterday", s.isSame(y, "day") && e.isSame(y, "day")]);
  }
  if (allowedKeys.includes("7d")) {
    candidates.push(["7d", s.isSame(a.clone().subtract(6, "day"), "day") && e.isSame(a, "day")]);
  }
  if (allowedKeys.includes("30d")) {
    candidates.push(["30d", s.isSame(a.clone().subtract(29, "day"), "day") && e.isSame(a, "day")]);
  }
  if (allowedKeys.includes("mtd")) {
    candidates.push(["mtd", s.isSame(a.clone().startOf("month"), "day") && e.isSame(a, "day")]);
  }
  for (const [k, ok] of candidates) {
    if (ok) return k;
  }
  return "custom";
}

function draftFromRange(startDate, endDate, isDemo) {
  const anchor = anchorDay(isDemo);
  const end = endDate && dayjs(endDate).isValid() ? dayjs(endDate) : anchor.clone();
  const start =
    startDate && dayjs(startDate).isValid() ? dayjs(startDate) : end.clone().subtract(6, "day");
  return [start, end];
}

function formatSummaryRange(startDate, endDate) {
  const s = dayjs(startDate);
  const e = dayjs(endDate);
  if (!s.isValid() || !e.isValid()) return null;
  return s.isSame(e, "day") ? s.format("D MMM YYYY") : `${s.format("D MMM")} – ${e.format("D MMM YYYY")}`;
}

// Helper: check if mobile based on window.innerWidth
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

function CustomDateModal({ open, onClose, onConfirm, currentRange, disabledDate, disabled, isDemo }) {
  const [range, setRange] = useState(null);
  const isMobile = useIsMobile(); // < 768px = mobile

  useEffect(() => {
    if (!open) return;
    const [start, end] = draftFromRange(currentRange?.start_date, currentRange?.end_date, isDemo);
    setRange([start, end]);
  }, [open, currentRange?.start_date, currentRange?.end_date, isDemo]);

  const canApply = range && range[0] && range[1] && range[0].isValid() && range[1].isValid() && !range[0].isAfter(range[1], "day");

  const handleApply = () => {
    if (!canApply) return;
    onConfirm([range[0], range[1]]);
    onClose();
  };

  const handleRangeChange = (dates) => {
    setRange(dates);
  };

  const handleFromChange = (date) => {
    setRange((prev) => [date, prev?.[1] ?? date]);
  };

  const handleToChange = (date) => {
    setRange((prev) => [prev?.[0] ?? date, date]);
  };

  const pickerProps = {
    className: "w-100 custom-range-picker",
    format: DISPLAY_DATE_FORMAT,
    disabled,
    disabledDate,
    allowClear: false,
    suffixIcon: <CalendarOutlined />,
    size: "large",
  };

  return (
    <Modal
      title="Select period"
      open={open}
      onCancel={onClose}
      closable={false}
      style={isMobile ? { top: 20 } : undefined}
      width={400}
      destroyOnClose={false}
      className="df-period-modal"
      maskClosable
      centered
      footer={
        <div className="gap-2 d-flex justify-content-center">
          <button onClick={onClose} className="btn btn-light">Cancel</button>
          <button type="button" onClick={handleApply} disabled={!canApply} className="btn btn-danger">Apply</button>
        </div>
      }
    >
      <WrapperModal className="">
        {/* <div className="fs-7 fw-bold mb-2">Select period</div> */}
        <div className="">
          {isMobile ? (
            <div className="d-flex flex-column gap-3">
              <div>
                <div className="fs-7 fw-bold mb-2">From</div>
                <DatePicker
                  {...pickerProps}
                  value={range?.[0]}
                  onChange={handleFromChange}
                  placeholder="From"
                />
              </div>
              <div>
                <div className="fs-7 fw-bold mb-2">To</div>
                <DatePicker
                  {...pickerProps}
                  value={range?.[1]}
                  onChange={handleToChange}
                  placeholder="To"
                />
              </div>
            </div>
          ) : (
            <RangePicker
              {...pickerProps}
              value={range}
              onChange={handleRangeChange}
              placeholder={["From", "To"]}
              placement="bottomLeft"
            />
          )}
        </div>
      </WrapperModal>
    </Modal>
  );
}

export default function DashFilterBar({
  otherRange,
  onOtherRangeChange,
  isDemoDateUser = false,
  disabledDate,
  isSpApiConnected = true,
  selectedMarketplace,
  marketplaceList = [],
  onMarketplaceChange,
  selectedAsins = [],
  setSelectedAsins,
  asinList = [],
  debouncedSearch,
  handleSearchBlur,
  handlePopupScroll,
  isSearching = false,
  hasMore = false,
  isDataSyncing = false,
  onCompareMetaChange,
}) {

  const mktOptions = GetMarketplaceList(marketplaceList || []);

  const [compareOn, setCompareOn] = useState(false);
  const [compareKey, setCompareKey] = useState("yesterday");
  const [customModalType, setCustomModalType] = useState(null);
  const [primaryIsCustom, setPrimaryIsCustom] = useState(false);

  const activePrimary = useMemo(
    () => inferPresetKey(otherRange, isDemoDateUser, PRIMARY_KEYS),
    [otherRange, isDemoDateUser]
  );

  const isCustomPrimaryActive = primaryIsCustom || activePrimary === "custom";

  const [compareRange, setCompareRange] = useState(() => {
    const d = getDefaultCompareForPrimary("today", null, isDemoDateUser);
    return { start_date: d.start_date, end_date: d.end_date };
  });

  const syncCompareToPrimary = useCallback(() => {
    if (isCustomPrimaryActive && otherRange?.start_date && otherRange?.end_date) {
      const prev = previousPeriodRange(otherRange.start_date, otherRange.end_date);
      if (prev) {
        setCompareKey("custom");
        setCompareRange(formatRangePair(prev));
        return;
      }
    }
    const effectivePrimary = isCustomPrimaryActive ? "custom" : activePrimary;
    const d = getDefaultCompareForPrimary(effectivePrimary, otherRange, isDemoDateUser);
    setCompareKey(d.key);
    setCompareRange({ start_date: d.start_date, end_date: d.end_date });
  }, [activePrimary, isCustomPrimaryActive, otherRange, isDemoDateUser]);

  useEffect(() => {
    if (!compareOn) return;
    syncCompareToPrimary();
  }, [
    compareOn,
    activePrimary,
    isCustomPrimaryActive,
    primaryIsCustom,
    otherRange?.start_date,
    otherRange?.end_date,
    syncCompareToPrimary,
  ]);

  useEffect(() => {
    onCompareMetaChange?.({
      enabled: compareOn,
      start_date: compareOn ? compareRange.start_date : undefined,
      end_date: compareOn ? compareRange.end_date : undefined,
    });
  }, [compareOn, compareRange.start_date, compareRange.end_date, onCompareMetaChange]);

  const applyPrimary = useCallback(
    (key) => {
      if (!isSpApiConnected) return;
      setPrimaryIsCustom(false);
      const r = rangeForPreset(key, isDemoDateUser);
      if (r) onOtherRangeChange?.(r);
    },
    [isSpApiConnected, isDemoDateUser, onOtherRangeChange]
  );

  const onPrimaryCustomConfirm = useCallback(
    (dates) => {
      if (!dates?.[0] || !dates?.[1]) return;
      setPrimaryIsCustom(true);
      onOtherRangeChange?.(dates);
      if (compareOn) {
        const prev = previousPeriodRange(dates[0], dates[1]);
        if (prev) {
          setCompareKey("custom");
          setCompareRange(formatRangePair(prev));
        }
      }
    },
    [onOtherRangeChange, compareOn]
  );

  const onCompareCustomConfirm = useCallback((dates) => {
    if (!dates?.[0] || !dates?.[1]) return;
    setCompareKey("custom");
    setCompareRange(formatRangePair(dates));
  }, []);

  const applyCompare = useCallback(
    (key) => {
      if (!isSpApiConnected) return;
      setCompareKey(key);
      if (key === "custom") return;
      const primaryForResolve = isCustomPrimaryActive ? "custom" : activePrimary;
      const r = resolveCompareRange(key, primaryForResolve, otherRange, isDemoDateUser);
      if (r) setCompareRange(formatRangePair(r));
    },
    [isSpApiConnected, activePrimary, isCustomPrimaryActive, otherRange, isDemoDateUser]
  );

  const onCompareToggle = useCallback(
    (checked) => {
      setCompareOn(checked);
      if (checked) syncCompareToPrimary();
    },
    [syncCompareToPrimary]
  );

  const summaryText = useMemo(() => {
    const p = isCustomPrimaryActive
      ? formatSummaryRange(otherRange?.start_date, otherRange?.end_date) || LABELS.custom
      : LABELS[activePrimary] || LABELS.custom;
    if (!compareOn) return p;
    const c =
      compareKey === "custom"
        ? formatSummaryRange(compareRange?.start_date, compareRange?.end_date) || LABELS.custom
        : LABELS[compareKey] || LABELS.custom;
    return `${p} vs previous ${c}`;
  }, [activePrimary, compareOn, compareKey, isCustomPrimaryActive, otherRange, compareRange]);

  const primaryCustomLabel = useMemo(() => {
    if (!isCustomPrimaryActive) return LABELS.custom;
    return formatSummaryRange(otherRange?.start_date, otherRange?.end_date) || LABELS.custom;
  }, [isCustomPrimaryActive, otherRange?.start_date, otherRange?.end_date]);

  const compareCustomLabel = useMemo(() => {
    if (compareKey !== "custom") return LABELS.custom;
    return formatSummaryRange(compareRange?.start_date, compareRange?.end_date) || LABELS.custom;
  }, [compareKey, compareRange?.start_date, compareRange?.end_date]);

  const disabled = !isSpApiConnected;

  const handleOpenPrimaryModal = useCallback(() => {
    if (disabled) return;
    setCustomModalType('primary');
  }, [disabled]);

  const handleCloseModal = useCallback(() => {
    setCustomModalType(null);
  }, []);

  const handleOpenCompareModal = useCallback(() => {
    if (disabled) return;
    if (isCustomPrimaryActive && otherRange?.start_date && otherRange?.end_date) {
      const prev = previousPeriodRange(otherRange.start_date, otherRange.end_date);
      if (prev) {
        setCompareKey("custom");
        setCompareRange(formatRangePair(prev));
      }
    }
    setCustomModalType("compare");
  }, [disabled, isCustomPrimaryActive, otherRange?.start_date, otherRange?.end_date]);

  return (
    <DashFilterBarRoot className="">
      <div className="df-search d-none d-lg-block">
        <Select
          variant="filled"
          mode="multiple"
          showSearch
          placeholder="Search SKU, Title or ASIN"
          className="w-100"
          size="middle"
          value={selectedAsins}
          onChange={setSelectedAsins}
          onSearch={debouncedSearch}
          onBlur={handleSearchBlur}
          onPopupScroll={handlePopupScroll}
          filterOption={false}
          loading={isSearching && isSpApiConnected}
          optionLabelProp="label"
          maxTagCount="responsive"
          disabled={disabled}
          notFoundContent={
            isSearching ? (
              <div className="text-center p-2">
                <Spin size="small" />
              </div>
            ) : null
          }
        >
          {asinList.map((item) => (
            <Option
              key={`${item.asin}:::${item.sku || ""}`}
              value={`${item.asin}:::${item.sku || ""}`}
              label={item.sku || item.asin}
            >
              <div className="d-flex align-items-center gap-3 py-1">
                <img
                  src={item.image || DEFAULT_PRODUCT_IMAGE}
                  alt=""
                  className="w-30px h-30px rounded border object-fit-contain"
                />
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div
                    className="fw-bolder text-dark text-truncate"
                    style={{ fontSize: "12px" }}
                    title={item.name}
                  >
                    {item.name}
                  </div>
                  <div className="fs-12 text-muted d-flex align-items-center" style={{ gap: "8px" }}>
                    <span className="badge badge-light-danger fw-bold" title={`ASIN: ${item.asin}`}>
                      {item.asin}
                    </span>
                    {item.sku && (
                      <span className="text-truncate" style={{ maxWidth: "160px" }} title={`SKU: ${item.sku}`}>
                        <small className="text-muted">
                          SKU: <span className="fw-bold text-dark">{item.sku}</span>
                        </small>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Option>
          ))}
          {isSearching && hasMore && (
            <Option disabled key="loading-more" className="text-center bg-light">
              <Spin size="small" /> <small className="ms-2">Loading more...</small>
            </Option>
          )}
        </Select>
      </div>

      <div className="df-pills">
        {PRIMARY_KEYS.filter((k) => k !== "custom").map((key) => (
          <button
            key={key}
            type="button"
            className={`df-pill ${
              !isCustomPrimaryActive && activePrimary === key ? "df-pill--primary-active" : ""
            }`}
            disabled={disabled}
            onClick={() => applyPrimary(key)}
          >
            {LABELS[key]}
          </button>
        ))}
        <button
          type="button"
          className={`df-pill  ${isCustomPrimaryActive ? "df-pill--primary-active" : ""}`}
          disabled={disabled}
          onClick={handleOpenPrimaryModal}
        >
          <CalendarOutlined className="me-1" />
          {primaryCustomLabel}
        </button>
      </div>

      <div className="df-divider" aria-hidden />

      <div className="d-flex align-items-center gap-2 flex-wrap">
        <Switch
          className="df-compare-switch"
          size="small"
          checked={compareOn}
          onChange={onCompareToggle}
          disabled={disabled}
        />
        <span className="df-compare-label">Compare</span>
      </div>

      {compareOn && (
        <div className="df-pills">
          {COMPARE_KEYS.filter((k) => k !== "custom").map((key) => (
            <button
              key={key}
              type="button"
              className={`df-pill ${compareKey === key ? "df-pill--compare-active" : ""}`}
              disabled={disabled}
              onClick={() => applyCompare(key)}
            >
              {COMPARE_LABELS[key]}
            </button>
          ))}
          <button
            type="button"
            className={`df-pill ${compareKey === "custom" ? "df-pill--compare-active" : ""}`}
            disabled={disabled}
            onClick={handleOpenCompareModal}
          >
            <CalendarOutlined className="me-1" />
            {compareCustomLabel}
          </button>
        </div>
      )}

      {/* <span className="df-summary ms-md-2">{summaryText}</span> */}

      <div className="ms-auto d-flex align-items-center gap-3 flex-wrap">
        {/* Marketplace and Live status components remain commented */}
      </div>

      {/* Custom Date Modals */}
      <CustomDateModal
        open={customModalType === "primary"}
        onClose={handleCloseModal}
        onConfirm={onPrimaryCustomConfirm}
        currentRange={otherRange}
        disabledDate={disabledDate}
        disabled={disabled}
        isDemo={isDemoDateUser}
      />

      <CustomDateModal
        open={customModalType === "compare"}
        onClose={handleCloseModal}
        onConfirm={onCompareCustomConfirm}
        currentRange={compareRange}
        disabledDate={disabledDate}
        disabled={disabled}
        isDemo={isDemoDateUser}
      />
    </DashFilterBarRoot>
  );
}